import { z } from 'zod';
import { BILLING_INTERVALS, PAID_PLAN_IDS, PLAN_IDS } from './account';
import { BUILT_IN_TOOLS } from './ai-tools';
import { ApiKeySchema } from './api-keys';
import { DESKTOP_PLATFORMS } from './desktop-installers';
import {
  CHANNELS,
  DESKTOP_SYSTEMS,
  INSTALLER_FROM,
  PLANS_OPENERS,
  RESULT_VIEWS,
  VERSION_ACTIONS,
} from './events';
import { LANGUAGES, LENGTHS, TONES } from './options';
import { RewritesSchema } from './rewrite';

// The admin dashboard: what Lyrmo's administrators see, and the vocabulary of the daily
// activity totals behind it.

/**
 * The header Lyrmo's own clients name themselves in, so the dashboard can tell the site's
 * requests from the desktop app's and the Chrome extension's.
 */
export const CLIENT_HEADER = 'x-lyrmo-client';

export const CLIENT_NAMES = ['web', 'desktop', 'extension'] as const;
export type ClientName = (typeof CLIENT_NAMES)[number];

/** Where a request came from: one of Lyrmo's clients, an API key, or anything else. */
export const ACTIVITY_SOURCES = [...CLIENT_NAMES, 'api', 'other'] as const;
export type ActivitySource = (typeof ACTIVITY_SOURCES)[number];

/**
 * How a rewrite or explanation ended: done, turned away for the daily credit limit, held back or
 * declined, failed, or abandoned by the client.
 */
export const ACTIVITY_OUTCOMES = [
  'completed',
  'limited',
  'refused',
  'failed',
  'cancelled',
] as const;
export type ActivityOutcome = (typeof ACTIVITY_OUTCOMES)[number];

/** Whose words they were: visitors without an account, or members on each plan. */
export const AUDIENCES = ['visitor', ...PLAN_IDS] as const;
export type Audience = (typeof AUDIENCES)[number];

/** How many days of activity the dashboard can show, ending today. */
export const DASHBOARD_RANGES = [7, 30, 90, 365] as const;
export type DashboardRange = (typeof DASHBOARD_RANGES)[number];

const count = z.int().nonnegative();
const day = z.iso.date().describe('A UTC day, as the daily credit allowances count them.');

export const ActivityTotalsSchema = z.object({
  signups: count.describe('Accounts made.'),
  rewrites: count.describe('Rewrites completed.'),
  words: count.describe('Input words in completed rewrites, not credits spent across features.'),
  explanations: count.describe('Explanations completed.'),
  limitReached: count.describe('Rewrites turned away for the daily credit limit.'),
  errors: count.describe('Rewrites held back, declined, or failed.'),
});
export type ActivityTotals = z.infer<typeof ActivityTotalsSchema>;

export const ActivityDaySchema = ActivityTotalsSchema.extend({
  day,
  words: z
    .record(z.enum(AUDIENCES), count)
    .describe('Input words in completed rewrites, by whose they were.'),
});
export type ActivityDay = z.infer<typeof ActivityDaySchema>;

const pair = z.object({ members: count, visitors: count });

export const AdminDashboardSchema = z.object({
  generatedAt: z.iso.datetime(),
  statsSince: day.nullable().describe('The first day of activity totals; null before any.'),
  accounts: z.object({
    total: count,
    today: count.describe('Accounts made today.'),
    withCustomTones: count.describe('Accounts with at least one custom tone.'),
    withApiKeys: count.describe('Accounts with at least one API key.'),
  }),
  apiKeys: z.object({ total: count, usedThisWeek: count }),
  writers: z
    .object({
      today: pair,
      week: pair.describe('Over the last 7 days, today included.'),
    })
    .describe('Accounts and visitors that used credits.'),
  today: z.object({ credits: count, rewrites: count }),
  visitors: z
    .object({
      today: count.describe('Visitors told apart today.'),
      newToday: count.describe('Of them, those first seen today.'),
      rewroteFirstVisit: count.describe('Of the new ones, those who completed a rewrite today.'),
      activeNow: count.describe('People who did anything in the last 5 minutes.'),
    })
    .nullable()
    .describe('Null unless visitors are told apart.'),
  subscriptions: z.object({
    entitled: count.describe(
      'Paid subscriptions with the use of their plan: active, trialing, or past due. Complimentary plans are counted by status alone.',
    ),
    cancelling: count.describe('Entitled subscriptions set to end with their billing period.'),
    plans: z
      .array(z.object({ plan: z.enum(PAID_PLAN_IDS), month: count, year: count }))
      .describe('Entitled subscriptions by plan and billing interval.'),
    statuses: z
      .array(z.object({ status: z.string(), count }))
      .describe('Every subscription by status, most first.'),
    monthlyRevenue: z
      .object({ amount: count.describe('In the smallest currency unit.'), currency: z.string() })
      .nullable()
      .describe(
        'What entitled subscriptions bring in a month at current prices, yearly ones as a twelfth; null when the prices can’t be loaded.',
      ),
  }),
  activity: z.object({
    days: z.int().positive(),
    from: day,
    to: day,
    daily: z.array(ActivityDaySchema),
    totals: ActivityTotalsSchema,
    previous: ActivityTotalsSchema.describe('The same totals for as many days before.'),
    outcomes: z
      .record(z.enum(ACTIVITY_OUTCOMES), count)
      .describe('Rewrite requests by how they ended.'),
    sources: z
      .record(z.enum(ACTIVITY_SOURCES), count)
      .describe('Completed rewrites by where they came from.'),
    tones: z.record(z.enum(TONES), count).describe('Rewrites in each built-in tone.'),
    lengths: z.record(z.enum(LENGTHS), count),
    languages: z.record(z.enum(LANGUAGES), count),
    withInstruction: count,
    withCustomTone: count.describe('Rewrites in one of the writer’s custom tones.'),
    averageMs: count.nullable().describe('How long a completed rewrite took, on average.'),
  }),
  topWriters: z
    .array(
      z.object({
        id: z.string(),
        email: z.string(),
        plan: z.enum(PLAN_IDS),
        week: count,
        today: count,
      }),
    )
    .describe('The accounts that used the most credits over the last 7 days.'),
  newestAccounts: z.array(
    z.object({
      id: z.string(),
      email: z.string(),
      plan: z.enum(PLAN_IDS),
      joinedAt: z.iso.datetime(),
      today: count,
    }),
  ),
  setup: z.object({
    rewriteProvider: z.enum(['openai', 'mock']),
    model: z
      .string()
      .nullable()
      .describe(
        'The rewrite and explanation model used by every plan; null with the mock provider.',
      ),
    billingProvider: z.enum(['paddle', 'mock']),
    paddleEnvironment: z.enum(['sandbox', 'production']).nullable(),
    emailProvider: z.enum(['resend', 'console']),
    creditsPerDay: z.record(z.enum(PLAN_IDS), count),
    freeCreditsPerNetwork: count,
  }),
});
export type AdminDashboard = z.infer<typeof AdminDashboardSchema>;

// Insights: which features writers use, how they sign in and subscribe, and the desktop app's
// downloads. Counted per UTC day, like the activity totals, and never by who did it.

/**
 * What writers ask Lyrmo to do: rewrite, explain a rewrite's changes, run an AI tool, suggest
 * other ways to say a change, transcribe dictation, and read text aloud.
 */
export const FEATURES = [
  'rewrite',
  'explanation',
  'tool',
  'alternatives',
  'dictation',
  'speech',
] as const;
export type Feature = (typeof FEATURES)[number];

/**
 * AI tool runs by tool: each built-in one, the writers' own tools together, never by name, and
 * instructions followed just once.
 */
export const TOOL_KINDS = [...BUILT_IN_TOOLS, 'own', 'once'] as const;
export type ToolKind = (typeof TOOL_KINDS)[number];

/** How a writer signs in: with the emailed link, or the code in the same email. */
export const SIGN_IN_METHODS = ['link', 'code'] as const;
export type SignInMethod = (typeof SIGN_IN_METHODS)[number];

/** Why a sign-in didn't work: the link or code no longer did, or the code was wrong. */
export const SIGN_IN_FAILURES = ['expired', 'wrong_code'] as const;
export type SignInFailure = (typeof SIGN_IN_FAILURES)[number];

/** How long after their first visit a new subscriber first paid. */
export const PAYMENT_DELAYS = [
  'same-day',
  '1-3-days',
  '4-7-days',
  '8-14-days',
  '15-30-days',
  'later',
] as const;
export type PaymentDelay = (typeof PAYMENT_DELAYS)[number];

const perFeature = z.record(z.enum(FEATURES), count);
const perPlatform = z.record(z.enum(DESKTOP_PLATFORMS), count);
const perPlan = z.array(z.object({ plan: z.enum(PAID_PLAN_IDS), month: count, year: count }));

export const InsightsTotalsSchema = z.object({
  features: perFeature.describe('Requests completed, by feature.'),
  limitReached: count.describe('Requests of any feature turned away for the daily credit limit.'),
  signups: count.describe('Accounts made.'),
  signInEmails: count.describe('Sign-in emails sent.'),
  signIns: count.describe('Sign-ins, with the link or the code.'),
  checkouts: count.describe('Checkouts started.'),
  subscribed: count.describe('Subscriptions started.'),
  downloads: perPlatform.describe('Desktop installers downloaded, by platform.'),
  plansOpened: count.describe('Times the plans were opened.'),
  plansChosen: count.describe('Times a plan was chosen in them.'),
  signInsOpened: count.describe('Times sign-in was opened.'),
  launches: count.describe('Times the desktop app started.'),
  firstLaunches: count.describe('Times the desktop app started for the first time on a computer.'),
});
export type InsightsTotals = z.infer<typeof InsightsTotalsSchema>;

export const InsightsDaySchema = InsightsTotalsSchema.extend({ day });
export type InsightsDay = z.infer<typeof InsightsDaySchema>;

export const AdminInsightsSchema = z.object({
  generatedAt: z.iso.datetime(),
  tracksVisitors: z
    .boolean()
    .describe(
      'Whether visitors are told apart by the visitor cookie (INSIGHTS_TRACK_VISITORS); without, every event is no one’s.',
    ),

  countedSince: z.object({
    activity: day.nullable().describe('The first day rewrites and explanations were counted.'),
    events: day
      .nullable()
      .describe('The first day everything else here was counted; null before anything was.'),
  }),
  days: z.int().positive(),
  from: day,
  to: day,
  daily: z.array(InsightsDaySchema),
  totals: InsightsTotalsSchema,
  previous: InsightsTotalsSchema.describe('The same totals for as many days before.'),
  features: z.object({
    outcomes: z
      .record(z.enum(FEATURES), z.record(z.enum(ACTIVITY_OUTCOMES), count))
      .describe('Requests of each feature by how they ended.'),
    sources: z
      .record(z.enum(FEATURES), z.record(z.enum(ACTIVITY_SOURCES), count))
      .describe('Completed requests of each feature by where they came from.'),
    audiences: z
      .record(z.enum(FEATURES), z.record(z.enum(AUDIENCES), count))
      .describe('Completed requests of each feature by whose they were.'),
    words: perFeature.describe(
      'Words of the text in completed requests: rewritten, formatted, transcribed, or read aloud.',
    ),
    averageMs: z
      .record(z.enum(FEATURES), count.nullable())
      .describe('How long a completed request took, on average; null without any.'),
    tools: z.record(z.enum(TOOL_KINDS), count).describe('Completed AI tool runs, by tool.'),
  }),
  signIns: z.object({
    emailsFailed: count.describe('Sign-in emails that couldn’t be sent.'),
    methods: z.record(z.enum(SIGN_IN_METHODS), count).describe('Sign-ins, by how.'),
    failures: z.record(z.enum(SIGN_IN_FAILURES), count).describe('Sign-ins that didn’t work.'),
  }),
  checkouts: z.object({
    started: perPlan.describe('Checkouts started, by plan and billing interval.'),
    subscribed: perPlan.describe('Subscriptions started, by plan and billing interval.'),
  }),
  downloads: z.object({
    versions: z
      .array(z.object({ platform: z.enum(DESKTOP_PLATFORMS), version: z.string(), count }))
      .describe('Installers downloaded, by platform and version, most first.'),
  }),
  writers: z
    .object({
      all: count.describe('People who completed a request of any feature.'),
      features: perFeature.describe('People who completed a request of each feature.'),
    })
    .nullable()
    .describe(
      'People told apart over the days shown, from the events kept 90 days: null when the days go back further, or before any.',
    ),
  versions: z
    .record(z.enum(VERSION_ACTIONS), z.record(RewritesSchema.keyof(), count))
    .describe('Versions copied on the site, or put in place by the desktop app, by version.'),
  views: z
    .record(z.enum(RESULT_VIEWS), count)
    .describe('Times a version was looked at more closely, by how.'),
  plansOpened: z.record(z.enum(PLANS_OPENERS), count).describe('What opened the plans.'),
  videos: z
    .array(z.object({ video: z.string(), started: count, finished: count }))
    .describe('Videos played on the site, most started first.'),
  desktop: z.object({
    versions: z
      .array(z.object({ version: z.string(), system: z.enum(DESKTOP_SYSTEMS), installs: count }))
      .describe(
        'Desktop installs that started over the last 7 days, by version and system, most first.',
      ),
    writers: count.describe('People who used the desktop app over the last 7 days.'),
    signedIn: count.describe('Of them, those signed in.'),
    funnel: z
      .object({
        firstStarts: count.describe('Installs that started for the first time in the days shown.'),
        wrote: count.describe('Of them, those that completed a request in the app.'),
        signedIn: count.describe('Of them, those signed in on the desktop.'),
      })
      .nullable()
      .describe('Null unless visitors are told apart and the days shown are within the 90 kept.'),
  }),
  installers: z
    .record(z.enum(INSTALLER_FROM), count)
    .describe('Installer downloads started on the site, by how the writer came to the page.'),
  activeWriters: z
    .object({
      today: count,
      week: count.describe('Over the last 7 days, today included.'),
      month: count.describe('Over the last 30 days, today included.'),
      actionsToday: count.describe(
        'Requests of any feature completed today, and versions copied or inserted.',
      ),
    })
    .nullable()
    .describe(
      'People who completed a request of any feature: null unless visitors are told apart.',
    ),
  writersDaily: z
    .array(z.object({ day, writers: count }))
    .nullable()
    .describe('People who completed a request of any feature on each day shown.'),
  openers: z
    .array(
      z.object({
        from: z.enum(PLANS_OPENERS),
        people: count.describe('People who opened the plans from here.'),
        subscribed: count.describe('Of them, those who subscribed within 7 days.'),
      }),
    )
    .nullable(),
  subscribers: z
    .object({
      total: count.describe('Accounts that subscribed in the days shown.'),
      traced: count.describe('Of them, those whose first visit is in the events kept.'),
      medianDays: z.number().nonnegative().nullable(),
      paidAfter: z.record(z.enum(PAYMENT_DELAYS), count),
      channels: z
        .array(z.object({ channel: z.enum(CHANNELS), subscribers: count }))
        .describe('How the traced ones first came, most first.'),
    })
    .nullable(),
  hours: z
    .array(z.array(count))
    .nullable()
    .describe(
      'Completed requests of any feature by weekday, Monday first, and UTC hour, from the events kept 90 days.',
    ),
  returns: z
    .object({
      visitors: z.array(
        z.object({
          week: day.describe('The Monday of the week they first came.'),
          people: count,
          back: z
            .array(count.nullable())
            .describe('Back 1 to 7 weeks after; null for weeks to come.'),
        }),
      ),
      accounts: z.array(
        z.object({
          week: day.describe('The Monday of the week the accounts were made.'),
          people: count,
          back: z.array(count.nullable()),
        }),
      ),
    })
    .nullable()
    .describe('Who comes back, week by week, from the events kept 90 days.'),
});
export type AdminInsights = z.infer<typeof AdminInsightsSchema>;

// Audience and first use: visits, where they came from, and what new visitors did first.

/** What visits are counted by, besides in all. */
export const VISIT_DIMENSIONS = [
  'source',
  'channel',
  'referrer',
  'campaign',
  'landing',
  'country',
  'device',
  'os',
  'browser',
  'language',
  'length',
] as const;
export type VisitDimension = (typeof VISIT_DIMENSIONS)[number];

/** How long a visit lasted, from its first page to the last thing done before the next. */
export const VISIT_LENGTHS = ['under-30s', '30s-2m', '2-10m', '10-30m', 'over-30m'] as const;
export type VisitLength = (typeof VISIT_LENGTHS)[number];

/** How many words a new visitor's first rewrite had. */
export const REWRITE_SIZES = ['under-50', '50-200', 'over-200'] as const;
export type RewriteSize = (typeof REWRITE_SIZES)[number];

const visitTotals = z.object({
  visits: count,
  visitors: count.describe('Told apart within each day, and added up over the days.'),
  newVisitors: count.describe('Not seen in the 90 days before.'),
  writers: count.describe('Of the visitors, those who also wrote that day.'),
});

export const AudienceDaySchema = visitTotals.extend({ day });
export type AudienceDay = z.infer<typeof AudienceDaySchema>;

const people = z
  .object({
    visitors: count.describe('Told apart over all the days.'),
    returning: count.describe('Of them, those who had come before the days began.'),
  })
  .nullable()
  .describe('From the events kept 90 days: null when the days go back further.');

export const AdminAudienceSchema = z.object({
  generatedAt: z.iso.datetime(),
  tracksVisitors: z
    .boolean()
    .describe(
      'Whether visitors are told apart by the visitor cookie (INSIGHTS_TRACK_VISITORS); without, every event is no one’s.',
    ),

  countedSince: day.nullable().describe('The first day visits were counted; null before any.'),
  days: z.int().positive(),
  from: day,
  to: day,
  daily: z.array(AudienceDaySchema),
  totals: visitTotals,
  previous: visitTotals.describe('The same totals for as many days before.'),
  people,
  previousPeople: people,
  breakdowns: z
    .record(
      z.enum(VISIT_DIMENSIONS),
      z.array(
        z.object({
          value: z.string(),
          visits: count,
          visitors: count,
          newVisitors: count,
          writers: count,
        }),
      ),
    )
    .describe('Visits by each dimension’s values, most first, the first 20.'),
  channels: z
    .array(
      z.object({
        channel: z.enum(CHANNELS),
        arrived: count,
        rewrote: count.describe('Rewrote on the day they first came.'),
        account: count.describe('Made an account within 30 days.'),
        paid: count.describe('Subscribed within 30 days.'),
      }),
    )
    .describe('New visitors by how they first came, and how many went on to each.'),
  referrers: z
    .array(
      z.object({
        referrer: z.string(),
        arrived: count,
        rewrote: count,
        account: count,
        paid: count,
      }),
    )
    .describe('New visitors by the site that sent them, the first 15, and how many went on.'),
  consent: z
    .object({
      countedSince: day.nullable().describe('The first day a choice was counted; null before.'),
      daily: z.array(z.object({ day, allowed: count, declined: count })),
      totals: z.object({ allowed: count, declined: count }),
      previous: z.object({ allowed: count, declined: count }),
      sources: z
        .array(z.object({ source: z.enum(ACTIVITY_SOURCES), allowed: count, declined: count }))
        .describe('Where the choices were made: the site’s notice, or the desktop app’s setting.'),
    })
    .describe('Choices about Lyrmo’s own insights, counted as no one’s, whoever made them.'),
});
export type AdminAudience = z.infer<typeof AdminAudienceSchema>;

// Traffic: each time the site is opened, counted by the server for everyone, without knowing who:
// visitors are told apart within a UTC day alone, so over days they're added up day by day.

/** What opens of the site are counted by, besides in all. */
export const OPEN_DIMENSIONS = [
  'page',
  'channel',
  'referrer',
  'campaign',
  'country',
  'device',
  'os',
  'browser',
] as const;
export type OpenDimension = (typeof OPEN_DIMENSIONS)[number];

const opens = z.object({
  opens: count,
  visitors: count.describe('Told apart within each day, and added up over the days.'),
});

export const AdminTrafficSchema = z.object({
  generatedAt: z.iso.datetime(),
  countedSince: day.nullable().describe('The first day opens were counted; null before any.'),
  days: z.int().positive(),
  from: day,
  to: day,
  daily: z.array(opens.extend({ day })),
  totals: opens,
  previous: opens.describe('The same for as many days before.'),
  hours: z
    .array(z.array(count).length(24))
    .length(7)
    .describe(
      'Visitors in each UTC hour of each weekday, Monday first, told apart within each hour of each day and added up over the days.',
    ),
  breakdowns: z
    .record(z.enum(OPEN_DIMENSIONS), z.array(opens.extend({ value: z.string() })))
    .describe('Visitors and opens by each dimension’s values, most visitors first, the first 20.'),
});
export type AdminTraffic = z.infer<typeof AdminTrafficSchema>;

/** The steps of first use, each counting the new visitors who reached it. */
export const COHORT_STEPS = [
  'arrived',
  'text',
  'rewrote',
  'looked',
  'copied',
  'returned',
  'account',
  'paid',
] as const;
export type CohortStep = (typeof COHORT_STEPS)[number];

/** What a new visitor did first, on the day they came. */
export const FIRST_ACTIONS = [
  'type',
  'paste',
  'file',
  'dictation',
  'version',
  'how-it-works',
  'desktop-apps',
  'pricing',
  'video',
  'rewrite',
  'nothing',
] as const;
export type FirstAction = (typeof FIRST_ACTIONS)[number];

/** How long new visitors took to finish their first rewrite, on the day they came. */
export const REWRITE_DELAYS = ['15s', '30s', '1m', '2m', '5m', 'later'] as const;
export type RewriteDelay = (typeof REWRITE_DELAYS)[number];

const steps = z.record(z.enum(COHORT_STEPS), count);

export const AdminFirstUseSchema = z.object({
  generatedAt: z.iso.datetime(),
  tracksVisitors: z
    .boolean()
    .describe(
      'Whether visitors are told apart by the visitor cookie (INSIGHTS_TRACK_VISITORS); without, every event is no one’s.',
    ),

  countedSince: day
    .nullable()
    .describe('The first day new visitors were counted; null before any.'),
  days: z.int().positive(),
  from: day,
  to: day,
  daily: z.array(z.object({ day, arrived: count, rewrote: count })),
  steps: steps.describe(
    'New visitors who first came in the days shown, and how many reached each step: text, rewriting, looking closer, and copying on the day they came; coming back within 7 days; an account or a subscription within 30.',
  ),
  previous: steps.describe('The same for as many days before.'),
  firstActions: z.record(z.enum(FIRST_ACTIONS), count),
  delays: z.record(z.enum(REWRITE_DELAYS), count),
  problems: z.object({
    limited: count.describe('Turned away by the credit limit on the day they came.'),
    failed: count.describe('Had a rewrite held back or fail on the day they came.'),
    slow: count.describe('Waited more than 10 seconds for their first rewrite.'),
    blocked: count.describe('Couldn’t dictate: the microphone was refused.'),
    unopened: count.describe('Asked for a sign-in email and didn’t sign in within a day.'),
  }),
  firstRewrites: z
    .object({
      defaultTone: count,
      otherTone: count,
      tones: z
        .array(z.object({ tone: z.string(), visitors: count }))
        .describe('The tones picked, most first: built-in ones, or "custom" for the writer’s own.'),
      instruction: count,
      translated: count,
      desktop: count,
      sizes: z.record(z.enum(REWRITE_SIZES), count),
    })
    .describe('How the first rewrites of those who rewrote the day they came were asked for.'),
  open: day
    .nullable()
    .describe(
      'New visitors since this day may still come back, sign up, or subscribe, so their steps can still grow.',
    ),
});
export type AdminFirstUse = z.infer<typeof AdminFirstUseSchema>;

// Accounts: finding one, everything about it, and what administrators can do to it.

/** Which accounts the directory lists: everyone, those on a plan, or subscriptions to look at. */
export const ACCOUNT_FILTERS = ['all', ...PLAN_IDS, 'past_due', 'cancelling'] as const;
export type AccountFilter = (typeof ACCOUNT_FILTERS)[number];

/** The directory's orders: by when accounts were made, or by credits over the last 7 days. */
export const ACCOUNT_SORTS = ['newest', 'oldest', 'active'] as const;
export type AccountSort = (typeof ACCOUNT_SORTS)[number];

export const ACCOUNTS_PAGE_SIZE = 25;

export const AccountListQuerySchema = z.object({
  query: z
    .string()
    .trim()
    .max(254)
    .default('')
    .describe('Part of an email, or a whole account id or billing customer id.'),
  filter: z.enum(ACCOUNT_FILTERS).default('all'),
  sort: z.enum(ACCOUNT_SORTS).default('newest'),
  page: z.coerce.number().pipe(z.int().positive().max(100_000)).default(1),
});
/** What the directory is asked for; each part left out takes its default. */
export interface AccountListQuery {
  readonly query?: string;
  readonly filter?: AccountFilter;
  readonly sort?: AccountSort;
  readonly page?: number;
}

const lastSignInAt = z.iso
  .datetime()
  .nullable()
  .describe('When the newest session still signed in began; null when none is.');

export const AccountSummarySchema = z.object({
  id: z.string(),
  email: z.string(),
  plan: z.enum(PLAN_IDS),
  joinedAt: z.iso.datetime(),
  today: count.describe('Credits used today.'),
  week: count.describe('Credits used over the last 7 days, today included.'),
  lastSignInAt,
});
export type AccountSummary = z.infer<typeof AccountSummarySchema>;

export const AdminAccountListSchema = z.object({
  accounts: z.array(AccountSummarySchema),
  total: count.describe('Accounts that match, over every page.'),
  page: z.int().positive(),
  pageSize: z.int().positive(),
});
export type AdminAccountList = z.infer<typeof AdminAccountListSchema>;

/** What administrators can do to an account, or to everyone on Free, as the log names it. */
export const ADMIN_ACTIONS = [
  'end-sessions',
  'reset-credits',
  'reset-free-credits',
  'revoke-api-key',
  'set-plan',
  'delete-account',
] as const;
export type AdminActionKind = (typeof ADMIN_ACTIONS)[number];

export const AdminActionSchema = z.object({
  id: z.string(),
  action: z.enum(ADMIN_ACTIONS),
  admin: z.string().describe('The email of the administrator who took it.'),
  accountId: z.string().nullable().describe('None for one taken on everyone on Free at once.'),
  accountEmail: z
    .string()
    .nullable()
    .describe('As it was then: the account may since have been deleted.'),
  detail: z.string().describe('What it affected, in words.'),
  at: z.iso.datetime(),
});
export type AdminAction = z.infer<typeof AdminActionSchema>;

export const AdminActionsSchema = z.object({
  actions: z.array(AdminActionSchema).describe('Newest first.'),
});

/** What giving everyone on Free today's credits back reset. */
export const FreeCreditsResetSchema = z.object({
  accounts: count.describe('Accounts on Free that had used credits today.'),
  visitors: count.describe('Browsers, signed out, that had used credits today.'),
  credits: count.describe('The credits they had used between them.'),
});
export type FreeCreditsReset = z.infer<typeof FreeCreditsResetSchema>;

/** The plan to give an account for free; Free takes back one given before. */
export const AccountPlanRequestSchema = z.object({ plan: z.enum(PLAN_IDS) });
export type AccountPlanRequest = z.infer<typeof AccountPlanRequestSchema>;

export const AdminSubscriptionSchema = z.object({
  id: z.string().describe('The payment provider’s id for it, or Lyrmo’s for a complimentary one.'),
  plan: z.enum(PAID_PLAN_IDS),
  interval: z.enum(BILLING_INTERVALS).nullable(),
  status: z.string().describe('As the payment provider names it, or "complimentary".'),
  currentPeriodEnd: z.iso.datetime().nullable(),
  cancelAtPeriodEnd: z.boolean(),
  updatedAt: z.iso.datetime(),
});
export type AdminSubscription = z.infer<typeof AdminSubscriptionSchema>;

export const AdminAccountSchema = z.object({
  id: z.string(),
  email: z.string(),
  joinedAt: z.iso.datetime(),
  plan: z.enum(PLAN_IDS),
  administrator: z.boolean().describe('Whether ADMIN_EMAILS names it.'),
  billingCustomerId: z.string().nullable(),
  subscriptions: z
    .array(AdminSubscriptionSchema)
    .describe('Every subscription, the one in use first, then by latest change.'),
  usage: z.object({
    limit: count.describe('Credits a day on its plan.'),
    daily: z
      .array(z.object({ day, credits: count }))
      .describe('Credits on each of the last 7 days, oldest first: counters are kept a week.'),
  }),
  sessions: z.object({ live: count, lastSignInAt }),
  apiKeys: z.array(ApiKeySchema),
  customTones: z.array(z.object({ id: z.string(), name: z.string(), updatedAt: z.iso.datetime() })),
  actions: z.array(AdminActionSchema).describe('What administrators did to it, newest first.'),
});
export type AdminAccount = z.infer<typeof AdminAccountSchema>;
